import EwaybillType from "@/enum/EwaybillType";
import LogisticsCompany from "@/enum/LogisticsCompany";
import UserType from "@/enum/UserType";
import { get, postJson } from "@/services/http/backend";
import { PagedResult } from "./types";

export enum EwaybillStatus {
  CANCELED = "CANCELED",
  NORMAL = "NORMAL",
}

export interface GetSharedEwaybillShippingAddressDetailsParams {
  /**
   * 使用面单的人所属公司 ID，为空表示不限制。
   */
  pageNo: number;
  pageSize: number;
  userType: UserType;
  consumerCompanyId?: number;
  ewaybillNo?: string;
  ewaybillAccountId?: number;
  status?: EwaybillStatus;
  providerCompanyId?: number;

  /** 格式: YYYY-MM-DD HH:mm:ss */
  startTime: string;
  endTime: string;
}

interface SharedEwaybillDetail {
  /**面单消费公司名称（微信名） */
  consumerCompanyName: string;

  /**面单消费公司备注名称 */
  consumerName: string;

  /**电子面单创建时间 */
  dateCreated: string;

  /**电子面单标识 ID */
  id: number;

  /**电子面单所属的物流公司 */
  logisticsCompany: LogisticsCompany;

  /**平台订单 ID，多个 ID 的时候用逗号（，）分隔。 */
  platformOrderIds: string;
  /**平台用户名（面单账号） */
  platformUsername: string;

  /**面单提供公司名称 */
  providerCompanyName: string;

  /**电子面单状态（正常单号或已取消单号） */
  status: EwaybillStatus;

  /**电子面单类型 */
  type: EwaybillType;
  /**电子面单运单号 */
  waybillNo: string;
}

/**
 * 查询已共享发货地址关联的电子面单列表
 */
export function getSharedEwaybillShippingAddressDetails(params: GetSharedEwaybillShippingAddressDetailsParams) {
  return get<PagedResult<SharedEwaybillDetail>>("/v2/shared_ewaybill_shipping_address/ewaybills", params);
}

export interface ExportEwaybillRecordParams {
  consumerCompanyId?: number;
  ewaybillAccountId?: number;
  ewaybillNo?: string;
  logisticsCompany?: LogisticsCompany;
  startTime: string;
  endTime: string;
  sharedAddressId?: number;
  userType?: UserType;
  status?: EwaybillStatus;
}

export enum EwaybillEwaybillRecordStatus {
  COMPLETED = "COMPLETED",
  ERROR = "ERROR",
  EXPIRED = "EXPIRED",
  PENDING = "PENDING",
  RUNNING = "RUNNING",
}

export interface ExportEwaybillRecord {
  /**网点名称 */
  branchName: string;

  /**消费导出电子面单的公司 ID */
  consumerCompanyId: number;

  /**导出记录创建时间 */
  dateCreated: string;

  /**导出后文件下载地址 */
  downloadUrl: string;

  /**导出记录截至时间 */
  endTime: string;

  /**导出电子面单所属的电子面单账号 ID，可能为空。 */
  ewaybillAccountId: number;
  /**电子面单运单号查询参数 */
  ewaybillNo: string;

  /**电子面单状态（正常单号或已取消单号） */
  ewaybillStatus: EwaybillStatus;
  /**导出记录唯一标识 ID */
  id: number;

  /**导出电子面单所属的物流公司 */
  logisticsCompany: LogisticsCompany;

  /**数据导出操作人员所属的公司 ID */
  operatorCompanyId: number;

  /**电子面单账号在平台的用户名，如果淘宝昵称，拼多多 ID。 */
  platformUsername: string;

  /**共享导出电子面单的公司 ID */
  providerCompanyId: number;

  /**导出电子面单所属的已共享的发货地址 ID */
  sharedEwaybillShippingAddressId: number;

  /**导出记录起始时间 */
  startTime: string;

  status: EwaybillEwaybillRecordStatus;
}

/**
 * 创建导出明细任务
 */
export function exportEwaybillRecord(params: ExportEwaybillRecordParams) {
  return postJson<ExportEwaybillRecord>("/ewaybill_export_record/create", params);
}

interface GetEwaybillRecordListParams {
  addressId?: number;
  pageNo: number;
  pageSize: number;
}

/**
 * 查询导出记录
 */
export function getEwaybillRecordList(params: GetEwaybillRecordListParams) {
  return get<PagedResult<ExportEwaybillRecord>>("/ewaybill_export_record/list", params);
}

export interface EwaybillConsumedMonthlyStatistic {
  /**统计年份 */
  year: number;

  /**统计月份 */
  month: number;

  /**已用单数 */
  allocatedQuantity: number;

  /**已取消数 */
  cancelQuantity: number;

  /**已共享的电子面单发货地址 ID */
  sharedEwaybillShippingAddressId: number;
}

/**
 * 查询近三个月面单使用额度统计
 */
export function getMonthlyStatistics(sharedEwaybillShippingAddressIds: number[] = []) {
  return get("/v2/shared_ewaybill_shipping_address/monthly_statistics", {
    sharedEwaybillShippingAddressIds: sharedEwaybillShippingAddressIds.join(","),
  }) as unknown as EwaybillConsumedMonthlyStatistic[];
}
